import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GuestData } from '../types';
import { updateRSVP } from '../services/api';
import { getWeddingConfig } from '../services/wedding-config';
import ScrollReveal from './animations/ScrollReveal';

interface Props {
  guestData: GuestData;
}

const RSVPSection: React.FC<Props> = ({ guestData }) => {
  const config = getWeddingConfig();
  const [attendanceStatus, setAttendanceStatus] = useState(guestData.attendanceStatus);
  const [confirmedSeats, setConfirmedSeats] = useState(
    guestData.confirmedSeats || guestData.seat
  );
  const [confirmedKidsSeats, setConfirmedKidsSeats] = useState(
    guestData.confirmedKidsSeats || 0
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleRSVP = async (status: 'yes' | 'no') => {
    setIsUpdating(true);
    try {
      const success = await updateRSVP(guestData.code, status, confirmedSeats, confirmedKidsSeats);
      if (success) {
        setAttendanceStatus(status);
        setShowConfirmation(true);
        setTimeout(() => setShowConfirmation(false), 4000);
      }
    } catch (error) {
      console.error('Failed to update RSVP:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatConfirmationDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const seatOptions = Array.from({ length: guestData.seat }, (_, i) => i + 1);
  const kidsSeatsOptions = Array.from({ length: (guestData.kidsSeats || 0) + 1 }, (_, i) => i);

  return (
    <section className="rsvp-section">
      {/* Background with gradient using existing palette */}
      <div className="rsvp-background">
        <div className="rsvp-background-overlay"></div>
      </div>

      {/* Header */}
      <ScrollReveal direction="up" delay={0.2}>
        <div className="rsvp-header">
          <h1 className="rsvp-subtitle">
            RSVP
          </h1>
          <h2 className="rsvp-title-script">
            Confirma tu Asistencia
          </h2>
        </div>
      </ScrollReveal>

      {/* Content */}
      <ScrollReveal direction="up" delay={0.4}>
        <div className="rsvp-content">
          <div className="rsvp-card">
          <div className="guest-info">
            <h3>Estimado/a {guestData.guest}</h3>
            <p className="guest-seats">
              
              Tienes reservados los siguientes lugares en nuestra celebración:
              </p>
              <p className="guest-seats">
              {guestData.seat === 1
                ? '1 lugar de adulto.'
                : `${guestData.seat} lugares de adulto`
              }
            </p>
            {guestData.kidsSeats && guestData.kidsSeats > 0 && (
              <p className="guest-seats">
                {guestData.kidsSeats === 1
                  ? 'Y 1 lugar para niño'
                  : `Y ${guestData.kidsSeats} lugares para niños`
                }
              </p>
            )}
            <p className="rsvp-deadline">
              Te agradecemos confirmar tu asistencia antes del {config.rsvp.deadline}
            </p>
          </div>

          {attendanceStatus === 'pending' ? (
            <div className="rsvp-form">
              {guestData.seat > 1 && (
                <div className="seats-selection">
                  <label className="seats-label">
                    ¿Cuántos adultos asistirán?
                  </label>
                  <select
                    value={confirmedSeats}
                    onChange={(e) => setConfirmedSeats(Number(e.target.value))}
                    className="seats-dropdown"
                  >
                    {seatOptions.map(num => (
                      <option key={num} value={num}>
                        {num === 1 ? '1 persona' : `${num} personas`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {guestData.kidsSeats && guestData.kidsSeats > 0 && (
                <div className="seats-selection">
                  <label className="seats-label">
                    ¿Cuántos niños asistirán?
                  </label>
                  <select
                    value={confirmedKidsSeats}
                    onChange={(e) => setConfirmedKidsSeats(Number(e.target.value))}
                    className="seats-dropdown"
                  >
                    {kidsSeatsOptions.map(num => (
                      <option key={num} value={num}>
                        {num === 0 ? 'Ninguno' : num === 1 ? '1 niño' : `${num} niños`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="rsvp-buttons">
                <motion.button
                  onClick={() => handleRSVP('yes')}
                  className="rsvp-btn rsvp-yes"
                  disabled={isUpdating}
                  whileHover={{ scale: isUpdating ? 1 : 1.05 }}
                  whileTap={{ scale: isUpdating ? 1 : 0.95 }}
                >
                  {isUpdating ? 'Confirmando...' : 'Confirmar Asistencia'}
                </motion.button>
                <motion.button
                  onClick={() => handleRSVP('no')}
                  className="rsvp-btn rsvp-no"
                  disabled={isUpdating}
                  whileHover={{ scale: isUpdating ? 1 : 1.05 }}
                  whileTap={{ scale: isUpdating ? 1 : 0.95 }}
                >
                  {isUpdating ? 'Procesando...' : 'No Podré Asistir'}
                </motion.button>
              </div>
            </div>
          ) : (
            <div className="rsvp-status">
              <div className={`status-card ${attendanceStatus}`}>
                {attendanceStatus === 'yes' ? (
                  <div className="status-content">
                    <h4 className="status-title">Asistencia Confirmada</h4>
                    <p className="status-details">
                      {(guestData.confirmedSeats || confirmedSeats) === guestData.seat
                        ? `Confirmaste ${(guestData.confirmedSeats || confirmedSeats) === 1 ? 'tu asistencia' : `la asistencia de ${guestData.confirmedSeats || confirmedSeats} adultos`}`
                        : `Confirmaste la asistencia de ${guestData.confirmedSeats || confirmedSeats} de ${guestData.seat} adultos`
                      }
                    </p>
                    {(guestData.confirmedKidsSeats || confirmedKidsSeats) > 0 && (
                      <p className="status-details">
                        {(guestData.confirmedKidsSeats || confirmedKidsSeats) === 1
                          ? 'Y 1 niño'
                          : `Y ${guestData.confirmedKidsSeats || confirmedKidsSeats} niños`
                        }
                      </p>
                    )}
                    {guestData.confirmationDate && (
                      <p className="confirmation-date">
                        Confirmado el {formatConfirmationDate(guestData.confirmationDate)}
                      </p>
                    )}
                    <p className="status-thanks">
                      ¡Nos emociona celebrar contigo este día tan especial! 
                      </p>
                      
                  </div>
                ) : (
                  <div className="status-content">
                    <h4 className="status-title">Asistencia Declinada</h4>
                    <p className="status-details">
                      Lamentamos que no puedas acompañarnos
                    </p>
                    {guestData.confirmationDate && (
                      <p className="confirmation-date">
                        Respuesta recibida el {formatConfirmationDate(guestData.confirmationDate)}
                      </p>
                    )}
                    <p className="status-thanks">
                      Te agradecemos habernos informado y esperamos verte pronto
                    </p>
                  </div>
                )}
              </div>
              
              <button 
                onClick={() => setAttendanceStatus('pending')} 
                className="change-rsvp-btn"
              >
                Modificar Respuesta
              </button>
            </div>
          )}

          {showConfirmation && (
            <div className="confirmation-message">
              {config.rsvp.confirmationMessage}
            </div>
          )}
          <div className="status-content">

          <div className="divider-small"></div>

          <p className="status-thanks">
                      Recuerda que este link fue generado especialmente para ti, evita compartirlo.
                    </p>
          </div>
          </div>
        </div>
      </ScrollReveal>

      {/* Bottom spacing */}
      <div className="rsvp-bottom"></div>
    </section>
  );
};

export default RSVPSection;